import { EntryConfig, ImageMeasurementConfig, StandCountMeasurementConfig, isKeypointModel } from "./entryConfig";
import type { EntryCollection } from "./entryCollection";
import {
  getDescriptorsForEntryConfig,
  getUnboundDescriptors,
  hasCustomLabel,
  type MeasurementDescriptor,
} from "@shared/types/measurementDescriptors";

// Image measurement metrics
export type CanonicalImageMeasurementMetric = 'length' | 'width' | 'count' | 'weight' | 'grade';
export type ImageMeasurementMetric = CanonicalImageMeasurementMetric | (string & {});

// Stand count measurement metrics
export type StandCountMetric = 'countPerM2' | 'rawCount' | 'avgLength' | 'avgWidth' | 'totalWeight';

// Row planted stand count metrics
export type RowPlantedStandCountMetric = 'plantsPerMeter' | 'numberPlants' | 'averageSpacing' | 'averageDiameter' | 'averageWidth' | 'averageGreenness';

// Union type for all measurement metrics
export type MeasurementMetric = ImageMeasurementMetric | StandCountMetric | RowPlantedStandCountMetric;

// Metric display configuration
export interface MetricDisplayConfig {
  value: MeasurementMetric;
  label: string;
  unit: string;
  // Decimal places to render in formatMetricValue. Defaults to 2 for cm, 1 otherwise.
  decimals?: number;
  conditionalCheck?: (config: EntryConfig) => boolean;
  descriptor?: MeasurementDescriptor;
}

const getImageMeasurementOptions = (config: EntryConfig) =>
  config.type === 'imageMeasurement'
    ? (config as ImageMeasurementConfig).imageMeasurementConfig?.imageMeasurementModelOptions
    : undefined;

const getImageMeasurementGrading = (config: EntryConfig) =>
  config.type === 'imageMeasurement'
    ? (config as ImageMeasurementConfig).imageMeasurementConfig?.grading
    : undefined;

// Image measurement metric configs
export const IMAGE_MEASUREMENT_METRICS: MetricDisplayConfig[] = [
  { value: 'length', label: 'Avg. Length', unit: 'cm' },
  { value: 'width', label: 'Avg. Width', unit: 'cm' },
  { value: 'count', label: 'Count', unit: '' },
  {
    value: 'weight',
    label: 'Total Weight',
    unit: 'g',
    conditionalCheck: (config) =>
      config.type === 'imageMeasurement' &&
      getImageMeasurementOptions(config)?.hasWeightMeasurement === true
  },
  {
    value: 'grade',
    label: 'Grade',
    unit: '',
    conditionalCheck: (config) =>
      config.type === 'imageMeasurement' &&
      Array.isArray(getImageMeasurementGrading(config)?.buckets) &&
      (getImageMeasurementGrading(config)?.buckets.length ?? 0) > 0
  }
];

const getStandCountImageMeasurementOptions = (config: EntryConfig) =>
  config.type === 'standCountMeasurement'
    ? (config as StandCountMeasurementConfig).standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions
    : undefined;

const getStandCountModelKind = (config: EntryConfig): string | undefined =>
  getStandCountImageMeasurementOptions(config)?.kind;

// Stand count measurement metric configs
export const STAND_COUNT_METRICS: MetricDisplayConfig[] = [
  { value: 'countPerM2', label: 'Count per m²', unit: '', decimals: 0 },
  {
    value: 'rawCount',
    label: 'Raw Count',
    unit: '',
    conditionalCheck: (config) =>
      config.type === 'standCountMeasurement' &&
      getStandCountModelKind(config) !== 'none'
  },
  {
    value: 'avgLength',
    label: 'Avg Length',
    unit: 'cm',
    conditionalCheck: (config) => {
      const kind = getStandCountModelKind(config);
      return config.type === 'standCountMeasurement' &&
        kind !== 'none' && !isKeypointModel(kind);
    }
  },
  {
    value: 'avgWidth',
    label: 'Avg Width',
    unit: 'cm',
    conditionalCheck: (config) => {
      const kind = getStandCountModelKind(config);
      return config.type === 'standCountMeasurement' &&
        kind !== 'none' && !isKeypointModel(kind);
    }
  },
  {
    value: 'totalWeight',
    label: 'Total Weight',
    unit: 'g',
    conditionalCheck: (config) =>
      config.type === 'standCountMeasurement' &&
      getStandCountModelKind(config) !== 'none' &&
      getStandCountImageMeasurementOptions(config)?.hasWeightMeasurement === true
  }
];

// Row planted stand count metric configs
export const ROW_PLANTED_STAND_COUNT_METRICS: MetricDisplayConfig[] = [
  { value: 'plantsPerMeter', label: 'Plants / m', unit: '' },
  { value: 'numberPlants', label: '# Plants', unit: '', decimals: 0 },
  { value: 'averageSpacing', label: 'Avg. Spacing', unit: 'cm' },
  { value: 'averageDiameter', label: 'Avg. Length', unit: 'cm' },
  { value: 'averageWidth', label: 'Avg. Width', unit: 'cm' },
  { value: 'averageGreenness', label: 'Avg. Greenness', unit: '' }
];

export function getDescriptorDisplayUnit(descriptor: MeasurementDescriptor): string {
  switch (descriptor.unit) {
    case 'meters':
      return 'cm';
    case 'grams':
      return 'g';
    case 'count':
      return '';
  }
}

export function convertDescriptorValueForDisplay(value: number, descriptor: MeasurementDescriptor): number {
  return descriptor.unit === 'meters' ? value * 100 : value;
}

export function getDescriptorDisplayDecimals(descriptor: MeasurementDescriptor): number {
  const unit = getDescriptorDisplayUnit(descriptor);
  if (unit === 'cm') return 2;
  if (descriptor.unit === 'count') return 0;
  return 1;
}

function getCanonicalBoundMetricConfig(
  canonicalMetric: MetricDisplayConfig,
  descriptor: MeasurementDescriptor
): MetricDisplayConfig {
  if (!hasCustomLabel(descriptor)) return canonicalMetric;

  return {
    ...canonicalMetric,
    label: descriptor.label,
    unit: getDescriptorDisplayUnit(descriptor),
    descriptor,
  };
}

function getDescriptorMetricConfig(descriptor: MeasurementDescriptor): MetricDisplayConfig {
  return {
    value: descriptor.key,
    label: descriptor.label,
    unit: getDescriptorDisplayUnit(descriptor),
    decimals: getDescriptorDisplayDecimals(descriptor),
    descriptor,
  };
}

function getImageMeasurementMetricsForConfig(entryConfig: EntryConfig): MetricDisplayConfig[] {
  const descriptors = getDescriptorsForEntryConfig(entryConfig);
  if (descriptors.length === 0) {
    return IMAGE_MEASUREMENT_METRICS.filter(metric =>
      !metric.conditionalCheck || metric.conditionalCheck(entryConfig)
    );
  }

  const descriptorsByBinding = new Map(descriptors.map((descriptor) => [descriptor.binding, descriptor]));
  const metricOrder: CanonicalImageMeasurementMetric[] = ['length', 'width', 'count', 'weight'];
  const metrics: MetricDisplayConfig[] = getUnboundDescriptors(entryConfig).map(getDescriptorMetricConfig);

  for (const metricValue of metricOrder) {
    const descriptor = descriptorsByBinding.get(metricValue);
    if (!descriptor) continue;

    const canonicalMetric = IMAGE_MEASUREMENT_METRICS.find((metric) => metric.value === metricValue);
    if (!canonicalMetric || (canonicalMetric.conditionalCheck && !canonicalMetric.conditionalCheck(entryConfig))) {
      continue;
    }

    metrics.push(getCanonicalBoundMetricConfig(canonicalMetric, descriptor));
  }

  const gradeMetric = IMAGE_MEASUREMENT_METRICS.find((metric) => metric.value === 'grade');
  if (gradeMetric && (!gradeMetric.conditionalCheck || gradeMetric.conditionalCheck(entryConfig))) {
    metrics.push(gradeMetric);
  }

  return metrics;
}

/**
 * Get available metrics for a given entry config
 */
export function getAvailableMetrics(entryConfig: EntryConfig): MetricDisplayConfig[] {
  if (entryConfig.type === 'imageMeasurement') {
    return getImageMeasurementMetricsForConfig(entryConfig);
  } else if (entryConfig.type === 'standCountMeasurement') {
    return STAND_COUNT_METRICS.filter(metric =>
      !metric.conditionalCheck || metric.conditionalCheck(entryConfig)
    );
  } else if (entryConfig.type === 'rowPlantedStandCount') {
    return ROW_PLANTED_STAND_COUNT_METRICS.filter(metric =>
      !metric.conditionalCheck || metric.conditionalCheck(entryConfig)
    );
  }
  return [];
}

function getEntryConfigRefId(entryCollection: EntryCollection): string | undefined {
  return typeof entryCollection.entryConfigRef === 'string'
    ? entryCollection.entryConfigRef
    : entryCollection.entryConfigRef?.id;
}

function getMatchingStandCountEntries(entryCollections: EntryCollection[], entryConfigId: string) {
  return entryCollections
    .filter(
      (entryCollection) =>
        entryCollection.type === 'standCountMeasurement' &&
        getEntryConfigRefId(entryCollection) === entryConfigId
    )
    .flatMap((entryCollection) => Object.values(entryCollection.standCountMeasurementEntries ?? {}));
}

function hasDisplayableStandCountDensity(entryCollections: EntryCollection[], entryConfigId: string): boolean {
  return getMatchingStandCountEntries(entryCollections, entryConfigId).some(
    (entry) =>
      entry.countPerSquareMeter != null ||
      (entry.imageMeasurement?.objectCount != null &&
        entry.standCountBoxLengthMeters != null &&
        entry.standCountBoxLengthMeters > 0 &&
        entry.standCountBoxWidthMeters != null &&
        entry.standCountBoxWidthMeters > 0)
  );
}

function hasStandCountRawCount(entryCollections: EntryCollection[], entryConfigId: string): boolean {
  return getMatchingStandCountEntries(entryCollections, entryConfigId).some(
    (entry) => entry.imageMeasurement?.objectCount != null
  );
}

/**
 * Get default metric for a given entry config type. Stand-count captures
 * normally open on density, but full-frame relabel captures intentionally lack
 * a measurement area. In that case raw count is the only metric with a value.
 */
export function getDefaultMetric(
  entryConfig: EntryConfig,
  entryCollections: EntryCollection[] = []
): MeasurementMetric | null {
  if (entryConfig.type === 'imageMeasurement') {
    return 'length';
  } else if (entryConfig.type === 'standCountMeasurement') {
    const canDisplayRawCount = getAvailableMetrics(entryConfig).some(({ value }) => value === 'rawCount');
    if (
      entryConfig.id &&
      canDisplayRawCount &&
      !hasDisplayableStandCountDensity(entryCollections, entryConfig.id) &&
      hasStandCountRawCount(entryCollections, entryConfig.id)
    ) {
      return 'rawCount';
    }
    return 'countPerM2';
  } else if (entryConfig.type === 'rowPlantedStandCount') {
    return 'plantsPerMeter';
  }
  return null;
}

const ALL_METRICS = [...IMAGE_MEASUREMENT_METRICS, ...STAND_COUNT_METRICS, ...ROW_PLANTED_STAND_COUNT_METRICS];

/**
 * Get display unit for a specific metric
 */
export function getMetricUnit(metric: MeasurementMetric, entryConfig?: EntryConfig): string {
  const metricConfig = entryConfig
    ? getAvailableMetrics(entryConfig).find(m => m.value === metric)
    : ALL_METRICS.find(m => m.value === metric);
  return metricConfig?.unit || '';
}

/**
 * Format value with appropriate unit
 */
export function formatMetricValue(value: number, metric: MeasurementMetric, entryConfig?: EntryConfig): string {
  const config = entryConfig
    ? getAvailableMetrics(entryConfig).find(m => m.value === metric)
    : ALL_METRICS.find(m => m.value === metric);
  const unit = config?.unit || '';
  const decimals = config?.decimals ?? (unit === 'cm' ? 2 : 1);
  const formattedValue = value.toFixed(decimals);
  return unit ? `${formattedValue} ${unit}` : formattedValue;
}

/**
 * Check if a metric is valid for an entry config type
 */
export function isValidMetricForConfig(metric: MeasurementMetric, entryConfig: EntryConfig): boolean {
  const availableMetrics = getAvailableMetrics(entryConfig);
  return availableMetrics.some(m => m.value === metric);
}
